var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * API service for validation-rule administration (CZNA-245, mvl-rule-admin).
 *
 * Consumes the rule-management contract from CZNA-244 (base
 * `czna-authorities/validation/rules`). Built against the documented contract;
 * the live endpoints land with the CZNA-244 deploy. Follows the project's
 * `useOkapiKy()` pattern — see `validationService.ts` and MEMORY: never put
 * `ky` into react hook deps; capture by closure or key react-query on params.
 *
 * Endpoints:
 *  - `fetchValidationRules`  — GET list with filters + paging.
 *  - `fetchValidationRule`   — GET single rule (404 → HTTPError).
 *  - `createValidationRule`  — POST MANUAL rule (201; 400 with MVL diagnostics).
 *  - `updateValidationRule`  — PUT (200; 400/404/409 optimistic-lock).
 *  - `deleteValidationRule`  — DELETE (204; ALEPH_CONVERTED → 409, caller disables instead).
 *  - `fetchValidationRuleHistory` — GET audit trail.
 *  - `fetchValidationRuleMeta`    — GET dropdown vocabularies.
 *  - `assistMvl`             — POST live MVL diagnostics + autocomplete.
 */
import { getHeaders } from './apiUtils';
import { API_PATHS, CZNA_VALIDATION_RULE_DETAIL, CZNA_VALIDATION_RULE_HISTORY, } from '../types/apiPaths';
/**
 * Serialise list filters into URLSearchParams. Empty / undefined values are
 * omitted so the query string stays clean and the backend applies its defaults.
 */
var buildListQuery = function (params) {
    var _a, _b;
    var sp = new URLSearchParams();
    if (params.query)
        sp.append('query', params.query);
    if (params.source)
        sp.append('source', params.source);
    if (typeof params.enabled === 'boolean')
        sp.append('enabled', String(params.enabled));
    if (params.recordType)
        sp.append('recordType', params.recordType);
    if (params.event)
        sp.append('event', params.event);
    if (params.sortBy)
        sp.append('sortBy', params.sortBy);
    if (params.sortDir)
        sp.append('sortDir', params.sortDir);
    sp.append('limit', String((_a = params.limit) !== null && _a !== void 0 ? _a : 100));
    sp.append('offset', String((_b = params.offset) !== null && _b !== void 0 ? _b : 0));
    return sp.toString();
};
/** GET /validation/rules — list with filters + paging. */
export var fetchValidationRules = function (ky, params) { return __awaiter(void 0, void 0, void 0, function () {
    var qs;
    return __generator(this, function (_a) {
        qs = buildListQuery(params);
        return [2 /*return*/, ky.get("".concat(API_PATHS.CZNA_VALIDATION_RULES, "?").concat(qs), {
                headers: getHeaders(),
            }).json()];
    });
}); };
/** GET /validation/rules/{id} — single rule. 404 surfaces as an HTTPError. */
export var fetchValidationRule = function (ky, id) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.get(CZNA_VALIDATION_RULE_DETAIL(id), {
                headers: getHeaders(),
            }).json()];
    });
}); };
/**
 * POST /validation/rules — create a MANUAL rule. 201 on success; 400 with MVL
 * compile diagnostics when the expression is invalid (caller inspects the
 * error body — see `assistMvl` for the live editor path).
 */
export var createValidationRule = function (ky, body) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.post(API_PATHS.CZNA_VALIDATION_RULES, {
                headers: getHeaders(),
                json: body,
            }).json()];
    });
}); };
/**
 * PUT /validation/rules/{id} — update a rule (incl. ALEPH_CONVERTED). The
 * `expectedUpdatedAt` token in the body drives optimistic locking; a stale
 * token returns 409 which the caller surfaces as a conflict banner.
 */
export var updateValidationRule = function (ky, id, body) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.put(CZNA_VALIDATION_RULE_DETAIL(id), {
                headers: getHeaders(),
                json: body,
            }).json()];
    });
}); };
/**
 * DELETE /validation/rules/{id} — removes a MANUAL rule (204). For an
 * ALEPH_CONVERTED rule the backend returns 409 (origin must be preserved);
 * the caller falls back to a `PUT { enabled: false }` disable instead.
 */
export var deleteValidationRule = function (ky, id) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, ky.delete(CZNA_VALIDATION_RULE_DETAIL(id), {
                    headers: getHeaders(),
                })];
            case 1:
                _a.sent();
                return [2 /*return*/];
        }
    });
}); };
/** GET /validation/rules/{id}/history — read-only audit trail. */
export var fetchValidationRuleHistory = function (ky, id) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.get(CZNA_VALIDATION_RULE_HISTORY(id), {
                headers: getHeaders(),
            }).json()];
    });
}); };
/** GET /validation/rules/meta — dropdown vocabularies for filters + form. */
export var fetchValidationRuleMeta = function (ky) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.get(API_PATHS.CZNA_VALIDATION_RULES_META, {
                headers: getHeaders(),
            }).json()];
    });
}); };
/**
 * POST /validation/mvl/assist — live MVL diagnostics + autocomplete for the
 * expression editor. Returns compile diagnostics (errors/warnings with offsets)
 * and completion suggestions at the requested cursor offset.
 */
export var assistMvl = function (ky, req) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.post(API_PATHS.CZNA_VALIDATION_MVL_ASSIST, {
                headers: getHeaders(),
                json: req,
            }).json()];
    });
}); };
/**
 * POST /validation/mvl/test — read-only rule tester (CZNA-229, AV-01). Runs one
 * MVL expression against an inline MARC record (single-record mode) and returns
 * an {@link MvlTestResult}. A syntactically invalid expression is NOT an HTTP
 * error: the backend returns 200 with `satisfied=false` and `diagnostics` filled.
 */
export var testMvl = function (ky, req) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.post(API_PATHS.CZNA_VALIDATION_MVL_TEST, {
                headers: getHeaders(),
                json: req,
            }).json()];
    });
}); };
/**
 * POST /validation/mvl/test (corpus-sample mode) — runs one MVL expression
 * against a small sample (server-capped at 50) of records of `recordType` and
 * returns an {@link MvlCorpusTestResult}. The `req` must carry `recordType` and
 * omit `record`.
 */
export var testMvlAgainstSample = function (ky, req) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, ky.post(API_PATHS.CZNA_VALIDATION_MVL_TEST, {
                headers: getHeaders(),
                json: req,
            }).json()];
    });
}); };
